import React, { useState, useCallback } from "react";
import { ChevronDown } from "react-feather";
import { RadioButton, RadioButtonOption } from "../radio-button/radio-button";
import { EvidenceCard } from "../evidence-card/evidence-card";
import type { EvidenceCardConfig } from "../criterion-question/criterion-question";
import "./criterion-detailed-question.scss";

export type { EvidenceCardConfig };

export interface CriterionDetailedQuestionProps {
  stepNumber?: number | string;
  title: string;
  badge?: React.ReactNode;

  radioName: string;
  radioOptions: RadioButtonOption[];
  radioValue?: string;
  onRadioChange?: (
    value: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  radioDisabled?: boolean;
  radioSelectedColor?: string;

  textareaName?: string;
  textareaValue?: string;
  onTextChange?: (
    value: string,
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => void;
  textareaPlaceholder?: string;
  textareaDisabled?: boolean;
  textareaRows?: number;
  textareaMaxLength?: number;
  textareaLabel?: string;
  /** Hide the textarea entirely (with reveal animation). */
  shouldHideTextBox?: boolean;
  /** When true, the textarea must contain non-empty content; otherwise a red error border is shown. */
  isTextRequired?: boolean;
  /** Optional error message shown below the textarea when validation fails. */
  textRequiredErrorMessage?: string;

  evidenceCards?: EvidenceCardConfig[];
  /** @deprecated Use evidenceCards instead */
  evidenceTitle?: string;
  /** @deprecated Use evidenceCards instead */
  evidenceStatements?: string[];
  /** @deprecated Use evidenceCards instead */
  evidenceCardBackGroundColor?: string;

  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;

  /** Optional content rendered between the header row and the radio options. */
  headerSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  id?: string;
}

const CriterionDetailedQuestion = ({
  stepNumber,
  title,
  badge,
  radioName,
  radioOptions,
  radioValue,
  onRadioChange,
  radioDisabled,
  radioSelectedColor,
  textareaName,
  textareaValue,
  onTextChange,
  textareaPlaceholder = "Enter your notes here…",
  textareaDisabled = false,
  textareaRows = 4,
  textareaMaxLength,
  textareaLabel,
  shouldHideTextBox = false,
  isTextRequired = false,
  textRequiredErrorMessage = "This field is required.",
  evidenceCards,
  evidenceTitle,
  evidenceStatements,
  evidenceCardBackGroundColor,
  collapsible = false,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  headerSlot,
  children,
  className,
  id,
}: CriterionDetailedQuestionProps) => {
  const isControlled = expandedProp !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expandedProp : internalExpanded;

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  }, [isExpanded, isControlled, onExpandedChange]);

  const titleId = id ? `${id}-title` : undefined;
  const textareaId = id ? `${id}-textarea` : undefined;
  const textareaLabelId = id ? `${id}-textarea-label` : undefined;

  const resolvedEvidenceCards: EvidenceCardConfig[] =
    evidenceCards != null && evidenceCards.length > 0
      ? evidenceCards
      : evidenceTitle != null ||
          (evidenceStatements != null && evidenceStatements.length > 0)
        ? [
            {
              title: evidenceTitle,
              statements: evidenceStatements,
              cardBackGroundColor: evidenceCardBackGroundColor,
            },
          ]
        : [];

  const hasEvidence = resolvedEvidenceCards.length > 0;

  const hasTextError =
    !shouldHideTextBox &&
    isTextRequired &&
    (textareaValue == null || textareaValue.trim().length === 0);

  return (
    <section
      id={id}
      className={["criterion-detailed-question", className]
        .filter(Boolean)
        .join(" ")}
      aria-labelledby={titleId}
    >
      {stepNumber != null && (
        <div className="criterion-detailed-question__step-column">
          <span className="criterion-detailed-question__step">
            {stepNumber}.
          </span>
        </div>
      )}

      <div className="criterion-detailed-question__body">
        <div className="criterion-detailed-question__header">
          <h3 id={titleId} className="criterion-detailed-question__title">
            {title}
          </h3>
          <div className="criterion-detailed-question__header-actions">
            {badge}
            {collapsible && (
              <button
                type="button"
                className={[
                  "criterion-detailed-question__toggle",
                  isExpanded
                    ? ""
                    : "criterion-detailed-question__toggle--collapsed",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <ChevronDown size={16} />
              </button>
            )}
          </div>
        </div>

        <div
          className={[
            "criterion-detailed-question__collapsible",
            isExpanded
              ? "criterion-detailed-question__collapsible--expanded"
              : "criterion-detailed-question__collapsible--collapsed",
          ].join(" ")}
          aria-hidden={!isExpanded}
        >
          <div className="criterion-detailed-question__collapsible-inner">
            {headerSlot != null && (
              <div className="criterion-detailed-question__header-slot">
                {headerSlot}
              </div>
            )}
            <div className="criterion-detailed-question__content">
              <RadioButton
                name={radioName}
                options={radioOptions}
                value={radioValue}
                onChange={onRadioChange}
                disabled={radioDisabled}
                selectedColor={radioSelectedColor}
              />

              <div
                className={[
                  "criterion-detailed-question__textarea-reveal",
                  shouldHideTextBox
                    ? "criterion-detailed-question__textarea-reveal--hidden"
                    : "criterion-detailed-question__textarea-reveal--visible",
                ].join(" ")}
                aria-hidden={shouldHideTextBox}
              >
                <div className="criterion-detailed-question__textarea-section">
                  {textareaLabel && (
                    <label
                      id={textareaLabelId}
                      htmlFor={textareaId}
                      className="criterion-detailed-question__textarea-label"
                    >
                      {textareaLabel}
                      {isTextRequired && (
                        <span
                          className="criterion-detailed-question__required-indicator"
                          aria-hidden="true"
                        >
                          {" "}
                          *
                        </span>
                      )}
                    </label>
                  )}
                  <textarea
                    id={textareaId}
                    name={textareaName}
                    className={[
                      "criterion-detailed-question__textarea",
                      hasTextError
                        ? "criterion-detailed-question__textarea--error"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    value={textareaValue}
                    placeholder={textareaPlaceholder}
                    disabled={textareaDisabled}
                    rows={textareaRows}
                    maxLength={textareaMaxLength}
                    required={isTextRequired}
                    aria-required={isTextRequired || undefined}
                    aria-invalid={hasTextError || undefined}
                    aria-labelledby={textareaLabel ? textareaLabelId : titleId}
                    onChange={(e) => onTextChange?.(e.target.value, e)}
                  />
                  {hasTextError && textRequiredErrorMessage && (
                    <span
                      className="criterion-detailed-question__textarea-error"
                      role="alert"
                    >
                      {textRequiredErrorMessage}
                    </span>
                  )}
                </div>
              </div>

              {hasEvidence &&
                resolvedEvidenceCards.map((card, index) => (
                  <EvidenceCard
                    key={index}
                    title={card.title}
                    statements={card.statements}
                    cardBackGroundColor={card.cardBackGroundColor}
                    titleAction={card.titleAction}
                  />
                ))}
            </div>

            {children != null && (
              <div className="criterion-detailed-question__children">
                {children}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { CriterionDetailedQuestion };
export default CriterionDetailedQuestion;
